import React, { useState, createRef } from 'react';
import { string, func, number, shape } from 'prop-types';
import PlacesAutocomplete, { geocodeByAddress } from 'react-places-autocomplete';

import Box from '@material-ui/core/Box';

import IconButton from '@material-ui/core/IconButton';
import FiberManualRecordOutlinedIcon from '@material-ui/icons/FiberManualRecordOutlined';
import LocationOnOutlined from '@material-ui/icons/LocationOnOutlined';
import Clear from '@material-ui/icons/Clear';

import Input from '../../../shared/Inputs/Input';
import FilteredSuggestions from './FilteredSuggestions';
import { track } from '../../../../modules/Analytics';

function LocationsAutocomplete({
  index,
  placeholder,
  location,
  numberOfLocations,
  selectAddress,
  removeAddress,
  canRemoveLocation,
  removeLocation,
}) {
  const [tempPlaceholder, setTempPlaceHolder] = useState(placeholder);
  const [address, setAddress] = useState(location?.formattedAddress || '');
  const [isAddressVerified, setIsAddressVerified] = useState(Boolean(location?.formattedAddress));
  const autocompleteRef = createRef();
  const isDestination = Boolean(index !== numberOfLocations - 1);

  const handleSelect = async addr => {
    try {
      setIsAddressVerified(true);
      const geocode = await geocodeByAddress(addr);
      if (geocode && geocode.length) {
        const [googleLocation] = geocode;
        const { lat, lng } = googleLocation.geometry.location;
        setAddress(addr);
        const getAddressParts = parts => {
          const types = [
            'street_number',
            'route',
            'locality',
            'neighborhood',
            'administrative_area_level_1',
            'country',
            'postal_code',
          ];
          return parts.reduce((acc, part) => {
            part.types.forEach(type => {
              if (types.includes(type)) {
                acc[type] = part.short_name;
              }
            });
            return acc;
          }, {});
        };
        const normalizeParts = parts => {
          const types = ['street_number', 'route', 'administrative_area_level_1', 'country', 'postal_code'];
          const missingParts = types.map(key => parts[key]).includes(undefined) || !(parts.locality || parts.neighborhood);
          if (missingParts) {
            throw new Error('missing parts');
          }
          return {
            line1: `${parts.street_number} ${parts.route}`,
            city: parts.locality ? parts.locality : parts.neighborhood,
            region: parts.administrative_area_level_1,
            country: parts.country,
            postalCode: parts.postal_code,
          };
        };
        selectAddress(index, {
          address: normalizeParts(getAddressParts(googleLocation.address_components)),
          formattedAddress: googleLocation.formatted_address,
          coordinates: {
            lat: lat(),
            lng: lng(),
          },
        });
      }
    } catch (e) {
      setIsAddressVerified(false);

      // eslint-disable-next-line no-console
      console.log('Error geocoding address', e);
    }
  };

  const changeAddress = input => {
    if (address === '' && input !== '') {
      if (index === 0) {
        track('Location - Starting Address - Search', {});
      } else if (index === numberOfLocations - 1) {
        track('Location - Destination Address - Search', {});
      } else {
        track('Location - Additional Address - Search', {});
      }
    }
    setAddress(input);
  };

  return (
    <Box width={{ md: 500 }} data-testid={`location-${index}-wrapper`}>
      <PlacesAutocomplete
        value={address}
        ref={autocompleteRef}
        onChange={changeAddress}
        onSelect={handleSelect}
        searchOptions={{
          types: ['address'],
          componentRestrictions: { country: 'us' },
        }}
      >
        {({ getInputProps, suggestions, getSuggestionItemProps }) => {
          const filteredSuggestions = suggestions.filter(suggestion => !suggestion.types.includes('route'));

          const noSuggestionsFound = filteredSuggestions.length === 0 && address.length > 4 && !isAddressVerified;

          return (
            <>
              <Box flexDirection="column" display="flex" width="100%" alignItems="center">
                <Box width="100%">
                  <Input
                    id={`locations-input-${index}`}
                    autoComplete="off"
                    outlined
                    label={tempPlaceholder}
                    startAdornment={
                      isDestination ? (
                        <FiberManualRecordOutlinedIcon color="secondary" />
                      ) : (
                        <LocationOnOutlined color="secondary" />
                      )
                    }
                    endAdornment={
                      canRemoveLocation(index, numberOfLocations) ? (
                        <IconButton
                          aria-label="delete"
                          onClick={() => {
                            track('Location - Remove Destination', {});
                            removeLocation(index);
                          }}
                        >
                          <Clear color="disabled" />
                        </IconButton>
                      ) : null
                    }
                    error={noSuggestionsFound ? 'Address not recognized' : ''}
                    {...getInputProps({
                      regex: new RegExp(/^\s*\S+(?:\s+\S+){2}/),
                      onBlur: () => {
                        if (location?.formattedAddress !== address) {
                          removeAddress(index);
                        }
                      },
                      onFocus: () => {
                        setTempPlaceHolder('');
                      },
                    })}
                  />
                </Box>
              </Box>
              <FilteredSuggestions
                filteredSuggestions={filteredSuggestions}
                getSuggestionItemProps={getSuggestionItemProps}
              />
            </>
          );
        }}
      </PlacesAutocomplete>
    </Box>
  );
}

LocationsAutocomplete.propTypes = {
  index: number.isRequired,
  placeholder: string.isRequired,
  selectAddress: func.isRequired,
  numberOfLocations: number.isRequired,
  location: shape({
    formattedAddress: string,
  }),
  removeAddress: func.isRequired,
  setHasError: func.isRequired,
  canRemoveLocation: func.isRequired,
  removeLocation: func.isRequired,
};

LocationsAutocomplete.defaultProps = {
  location: {},
};

export default LocationsAutocomplete;
